import React, { useState, useEffect } from 'react';
import { CreateTableOld as CreateTable } from '../../../EmployeeTables/EmployeeTable';
import axios from '../../../../../../api/apiClient';
import { token, domain } from '../../../../../Security';
import { TAXABLESTATUS } from '../../../../../../utils/enums';

// icons
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button } from '../../../../../../common/APICommon';
import { FEATURES } from '../../../../../../config/features';

export function EmployeeAccountBankTab({
  employeeBankAccounts,
  setEmployeeBankAccounts,
  isEditMode,
}) {
  const [data, setData] = useState([]);
  const [bankList, setBankList] = useState([]);

  // Modal States
  // Consolidated: 'showForm' handles visibility for both Add and Edit
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editIndex, setEditIndex] = useState(-1);

  // Fetch Master Bank List
  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const response = await axios.get(`${domain}/api/Bank`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setBankList(response.data);
      } catch (error) {
        console.error("Error fetching bank list:", error);
      }
    };
    fetchBanks();
  }, []);

  // Sync props to local state
  useEffect(() => {
    setData(employeeBankAccounts || []);
  }, [employeeBankAccounts]);

  // Form Handlers
  const openForm = () => {
    setEditData(null); // Ensure we are in "Add" mode
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditData(null);
    setEditIndex(-1);
  };

  // Helper: Find Bank Name from List (if missing in data) or use existing
  const getBankName = (item) => {
    if (item.bankName) return item.bankName;
    const bank = bankList.find(b => b.id === item.bankId || b.id === parseInt(item.bankId));
    return bank ? bank.bankName : "";
  };

  // --- CRUD Operations ---

  const addBankAccount = (bankId, bankAccount, taxableStatus) => {
    const selectedBank = bankList.find(b => b.id === parseInt(bankId));

    const newData = {
      id: 0,
      employeePersonalInformationId: 0,
      bankId: parseInt(bankId),
      bankName: selectedBank ? selectedBank.bankName : "",
      bankAccount: bankAccount,
      taxableStatus: taxableStatus
    };

    const updatedList = [...data, newData];
    setData(updatedList);
    setEmployeeBankAccounts(updatedList);
    closeForm();
  };

  const updateBankAccount = (bankId, bankAccount, taxableStatus) => {
    const selectedBank = bankList.find(b => b.id === parseInt(bankId));

    const updatedItem = {
      ...editData,
      bankId: parseInt(bankId),
      bankName: selectedBank ? selectedBank.bankName : editData.bankName,
      bankAccount: bankAccount,
      taxableStatus: taxableStatus
    };

    const updatedList = [...data];
    if (editIndex > -1) {
      updatedList[editIndex] = updatedItem;
      setData(updatedList);
      setEmployeeBankAccounts(updatedList);
    }
    closeForm();
  };

  const deleteBankAccount = (index) => {
    if (!window.confirm("Are you sure you want to remove this bank account?")) return;
    const updatedList = [...data];
    updatedList.splice(index, 1);
    setData(updatedList);
    setEmployeeBankAccounts(updatedList);
  };

  const editHandleClick = (item, index) => {
    setEditData(item);
    setEditIndex(index);
    setShowForm(true); // Reuses the same form state
  };

  // --- Table Configuration ---
  const header = [
    "Bank Name",
    "Account Number",
    ...(FEATURES.EMPLOYEE_ACCOUNT_EMPLOYEE_BANK_TAXABLE_STAT ? ["Taxable Status"] : []),
    ...( isEditMode ? ["Action"] : [])
  ];
  const accessor = [
    "bankNameDisplay",
    "bankAccount",
    ...(FEATURES.EMPLOYEE_ACCOUNT_EMPLOYEE_BANK_TAXABLE_STAT ? ["taxableStatus"] : []),
    ...( isEditMode ? ["manage"] : [])
  ];

  const tableData = data.map((item, index) => ({
    ...item,
    bankNameDisplay: getBankName(item),
    bankAccount: item.bankAccount,
    manage: isEditMode ? (
      <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
        <div
          onClick={() => editHandleClick(item, index)}
          style={{ cursor: "pointer", color: "#f0ad4e" }}
        >
          <EditIcon fontSize="small" />
        </div>
        <div
          onClick={() => deleteBankAccount(index)}
          style={{ cursor: "pointer", color: "#d9534f" }}
        >
          <DeleteIcon fontSize="small" />
        </div>
      </div>
    ) : null
  }));

  return (
    <div className="accounts-tab column mt-3">
      <div className="emergency-tab">
        <div className="emergency-tab-top" style={{ display: isEditMode ? "flex" : "none", justifyContent: "start", marginTop: "15px" }}>
          <Button
            icon={<AccountBalanceIcon />}
            name={"ADD BANK"}
            className={"bg-green-500 hover:bg-green-600 text-white mb-2"}
            onClick={openForm}
          />
        </div>

        <div className="emergency-tab-body">
          <div className="employeesTab-table container">
            <CreateTable
              dataList={tableData}
              dataHeader={header}
              dataAccessor={accessor}
              editHandleClick={(item) => console.log(item)}
              isEditMode={isEditMode}
            />
          </div>
        </div>

        {/* Unified Form for both Add and Edit */}
        <AddEditBankForm
          isOpen={showForm}
          onClose={closeForm}
          editData={editData}
          bankList={bankList}
          addBankAccount={addBankAccount}
          updateBankAccount={updateBankAccount}
        />
      </div>
    </div>
  );
}

// --- Consolidated Component ---
export function AddEditBankForm({
  isOpen,
  onClose,
  editData,
  bankList,
  addBankAccount,
  updateBankAccount
}) {
  const [bankId, setBankId] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [taxableStatus, setTaxableStatus] = useState(TAXABLESTATUS.NONE);

  // Derived state to determine mode
  const isEdit = !!editData;

  useEffect(() => {
    if (isOpen) {
      if (isEdit) {
        // Edit Mode: Populate Fields
        let currentId = editData.bankId;
        // Fallback: match by name if ID is missing (legacy data support)
        if (!currentId && editData.bankName) {
          const found = bankList.find(b => b.bankName === editData.bankName);
          if (found) currentId = found.id;
        }

        setBankId(currentId || '');
        setBankAccount(editData.bankAccount || '');
        setTaxableStatus(editData.taxableStatus || TAXABLESTATUS.NONE);
      } else {
        // Add Mode: Reset Fields
        setBankId('');
        setBankAccount('');
        setTaxableStatus(TAXABLESTATUS.NONE);
      }
    }
  }, [isOpen, editData, bankList]);

  const handleSubmit = () => {
    if (!bankId || !bankAccount) {
      alert("Please fill up all required fields");
      return;
    }

    if (isEdit) {
      updateBankAccount(bankId, bankAccount, taxableStatus);
    } else {
      addBankAccount(bankId, bankAccount, taxableStatus);
    }
  };

  return (
    <div className={`modal-overlay ${isOpen ? 'open' : ''}`}>
      {isOpen && (
        <div className="modal">
          <div className="add-employees-form">
            <div className="popUp-header row">
              <div className='popUp-title'>
                <h3 style={{ color: "#668fca" }}>
                  {isEdit ? "Edit Bank Account" : "Add Bank Account"}
                </h3>
              </div>
              <div className='popUp-close'><CloseIcon onClick={onClose} /></div>
            </div>
            <div className="popUp-body">
              <div className="popUp-item info-item column">
                <div className="info-item-label">Bank Name</div>
                <div className="info-item-input">
                  <select className="form-control" value={bankId} onChange={(e) => setBankId(e.target.value)}>
                    <option value="">-- Select Bank --</option>
                    {bankList.map((bank) => (
                      <option key={bank.id} value={bank.id}>{bank.bankName}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="popUp-item info-item column">
                <div className="info-item-label">Account Number</div>
                <div className="info-item-input">
                  <input
                    placeholder="Account Number"
                    value={bankAccount}
                    onChange={(e) => setBankAccount(e.target.value)}
                  />
                </div>
              </div>

              {FEATURES.EMPLOYEE_ACCOUNT_EMPLOYEE_BANK_TAXABLE_STAT && (
                <div className="popUp-item info-item column">
                  <div className="info-item-label">Taxable Status</div>
                  <div className="info-item-input">
                    <select className="form-control" value={taxableStatus} onChange={(e) => setTaxableStatus(e.target.value)}>
                      {Object.entries(TAXABLESTATUS).map(([key, value]) => (
                        <option key={key} value={value}>{value}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <div className="tab-footer">
                <div className="buttons row" style={{ justifyContent: "end" }}>
                  <button className="save row" onClick={handleSubmit}>
                    {isEdit ? "UPDATE" : "SAVE"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}